import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getFirestore, collection, query, getDocs } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartColumn, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const normalizeText = (value) => String(value || '').trim()

const SessionTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="sessionDemandTooltip">
      <strong>{label}</strong>
      <div className="sessionDemandTooltipRow">
        <span>Users</span>
        <strong>{formatCount(row.count)}</strong>
      </div>
      <div className="sessionDemandTooltipRow">
        <span>Share</span>
        <strong>{row.shareLabel}</strong>
      </div>
    </div>
  )
}

function SessionTimeDemand() {
  const db = getFirestore(app)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const q = query(collection(db, 'users'))
      const qs = await getDocs(q)

      const counts = {}

      qs.forEach((docItem) => {
        const data = docItem.data()
        const session = normalizeText(data.sessionTime) || 'Unspecified'
        counts[session] = (counts[session] || 0) + 1
      })

      const formatted = Object.keys(counts)
        .map((key) => ({
          sessionTime: key,
          count: counts[key]
        }))
        .sort((first, second) => {
          if (second.count !== first.count) {
            return second.count - first.count
          }

          return first.sessionTime.localeCompare(second.sessionTime)
        })

      setRows(formatted)
    } catch (fetchError) {
      console.error('Failed to load session time demand:', fetchError)
      setRows([])
      setError('We could not load the session time demand right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()

    if (!search) {
      return rows
    }

    return rows.filter((row) => row.sessionTime.toLowerCase().includes(search))
  }, [rows, searchTerm])

  const totalUsers = useMemo(() => {
    return rows.reduce((sum, row) => sum + row.count, 0)
  }, [rows])

  const topRows = useMemo(() => {
    return filteredRows.slice(0, 8)
  }, [filteredRows])

  const topSession = rows[0] || null
  const topShare = totalUsers > 0 && topSession ? (topSession.count / totalUsers) * 100 : 0
  const hasRows = filteredRows.length > 0
  const emptyMessage = rows.length
    ? 'No session time values match the current search.'
    : 'No session time data was found yet.'

  const chartData = useMemo(() => {
    return topRows.map((row) => ({
      ...row,
      shareLabel: totalUsers > 0 ? `${((row.count / totalUsers) * 100).toFixed(1)}%` : '0.0%'
    }))
  }, [topRows, totalUsers])

  return (
    <div className="mainContainer sessionTimeDemandPage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="sessionTimeDemandContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Session Time Demand</h4>
              <p className="appsSubtext">
                See which session times are most common, how concentrated the demand is, and which options may need more attention.
              </p>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Total users</span>
                <strong>{formatCount(totalUsers)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Session options</span>
                <strong>{formatCount(rows.length)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Top session</span>
                <strong>{topSession ? topSession.sessionTime : '—'}</strong>
              </div>
              <div className="appsStatChip">
                <span>Top share</span>
                <strong>{topSession ? `${topShare.toFixed(1)}%` : '0.0%'}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard sessionDemandFiltersCard">
            <div className="appsSearch input-group sessionDemandSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search session time..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="sessionDemandFiltersMeta">
              <span>{formatCount(filteredRows.length)} session values visible</span>
              <span>{formatCount(topRows.length)} shown in the chart</span>
            </div>
            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="sessionDemandGrid">
            <div className="appsTableCard sessionDemandChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                  Top session times
                </h6>
                <span>Ranked by demand</span>
              </div>
              <div className="sessionDemandChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading session time demand...</span>
                    </Spinner>
                    <p>Loading session time demand...</p>
                  </div>
                ) : hasRows ? (
                  <div className="sessionDemandChartBox">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                        <XAxis
                          dataKey="sessionTime"
                          interval={0}
                          angle={-18}
                          textAnchor="end"
                          height={72}
                          tick={{ fontSize: 12, fill: '#475569' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                        />
                        <Tooltip content={<SessionTooltip />} />
                        <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                        <Bar dataKey="count" name="Users" fill="#0f766e" radius={[10, 10, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>{emptyMessage}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="appsInsightsCard sessionDemandInsightsCard">
              <div className="appsInsightsHeader">
                <h6>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Demand snapshot
                </h6>
                <span>{formatCount(topRows.length)} shown</span>
              </div>

              <div className="appsInsightsList">
                {topRows.length ? (
                  topRows.map((row) => {
                    const share = totalUsers > 0 ? (row.count / totalUsers) * 100 : 0

                    return (
                      <div className="appsInsightsItem" key={row.sessionTime}>
                        <div>
                          <strong>{row.sessionTime}</strong>
                          <span>{formatCount(row.count)} users</span>
                        </div>
                        <small>{share.toFixed(1)}%</small>
                      </div>
                    )
                  })
                ) : (
                  <div className="incomeStateCard">
                    <p>No session times match the current search.</p>
                  </div>
                )}
              </div>

              <div className="sessionDemandMiniStats">
                <div className="sessionDemandMiniStat">
                  <span>Most requested</span>
                  <strong>{topSession ? topSession.sessionTime : '—'}</strong>
                </div>
                <div className="sessionDemandMiniStat">
                  <span>Concentration</span>
                  <strong>{topSession ? `${topShare.toFixed(1)}%` : '0.0%'}</strong>
                </div>
              </div>

              <p className="appsInsightsMore">
                Use the search box to narrow a specific session time, or refresh after new registrations come in.
              </p>
            </div>
          </div>

          <div className="appsTableCard sessionDemandTableCard">
            <div className="appsTableHeader">
              <h6>Session time table</h6>
              <span>{formatCount(filteredRows.length)} results</span>
            </div>
            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading session time table...</span>
                  </Spinner>
                  <p>Loading session time table...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable sessionDemandTable">
                  <thead>
                    <tr>
                      <th>Session Time</th>
                      <th>Users</th>
                      <th>Share</th>
                      <th>Demand</th>
                    </tr>
                  </thead>
                  <tbody>
                      {hasRows ? (
                        filteredRows.map((row) => {
                          const share = totalUsers > 0 ? (row.count / totalUsers) * 100 : 0
                          const topCount = topSession?.count || 0
                          const demandWidth = topCount > 0 ? Math.max((row.count / topCount) * 100, 8) : 8

                          return (
                            <tr key={row.sessionTime}>
                              <td>
                                <strong>{row.sessionTime}</strong>
                              </td>
                              <td>{formatCount(row.count)}</td>
                              <td>{share.toFixed(1)}%</td>
                              <td>
                                <div className="sessionDemandTrack" aria-hidden="true">
                                  <span
                                    className="sessionDemandTrackFill"
                                    style={{ width: `${Math.min(demandWidth, 100)}%` }}
                                  />
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      ) : (
                      <tr>
                        <td colSpan="4" className="appsEmptyState">
                          {emptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SessionTimeDemand
